import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  getDocs,
  query,
  updateDoc,
  where,
  type DocumentReference,
} from "firebase/firestore";
import { CircularProgress, Tooltip } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { useAtomValue } from "jotai";
import { DateTime } from "luxon";

import { PlainCard } from "@/components/settings_components";
import { AuthContext } from "@/auth/AuthContext";
import { customFirestore } from "@/firebase";
import { companyDataAtom } from "@/routes/test-run/test-run-atoms";
import type {
  BillingConfig,
  CompanyBilling,
  Session,
  SessionLog,
} from "@/utils/types";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const fmtInt = (n: number) => Math.round(n).toLocaleString();
const fmtMoney = (n: number) => `$${(n || 0).toFixed(2)}`;
const fmtDur = (s: number | null) => {
  if (s == null) return "—";
  const r = Math.round(s);
  if (r >= 60) {
    const m = Math.floor(r / 60);
    const ss = r % 60;
    return `${m}m ${ss < 10 ? "0" : ""}${ss}s`;
  }
  return `${r}s`;
};

// Round a rough step up to a "nice" 1/2/2.5/5 × 10ⁿ value for chart axes.
const niceStep = (rough: number) => {
  if (rough <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(rough)));
  const frac = rough / pow;
  const niceFrac =
    frac <= 1 ? 1 : frac <= 2 ? 2 : frac <= 2.5 ? 2.5 : frac <= 5 ? 5 : 10;
  return niceFrac * pow;
};
const fmtAxis = (v: number) =>
  v >= 1000
    ? `${+(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k`
    : `${Math.round(v)}`;

// "MM_YYYY" key for the current month, matching the company usage maps.
const currentUsageKey = () => {
  const now = new Date();
  const mm = `${now.getMonth() + 1}`.padStart(2, "0");
  return `${mm}_${now.getFullYear()}`;
};

// "MM_YYYY" -> sortable ordinal (year*12 + monthIndex), or null if malformed.
const keyToOrd = (key: string): number | null => {
  const m = /^(\d{2})_(\d{4})$/.exec(key);
  if (!m) return null;
  return Number(m[2]) * 12 + (Number(m[1]) - 1);
};

// Resolve the billing config effective for a given "MM_YYYY" month: the latest
// period override at or before that month, otherwise `default`.
const resolveBilling = (
  billing: CompanyBilling | undefined,
  monthKey: string
): BillingConfig | null => {
  if (!billing) return null;
  const target = keyToOrd(monthKey);
  let best: { ord: number; cfg: BillingConfig } | null = null;
  if (target != null) {
    for (const [k, cfg] of Object.entries(billing)) {
      if (k === "default" || !cfg) continue;
      const ord = keyToOrd(k);
      if (ord == null || ord > target) continue;
      if (!best || ord > best.ord) best = { ord, cfg };
    }
  }
  return best?.cfg ?? billing.default ?? null;
};

// Step classification mirrors src/routes/sessions/info.tsx:
//  - local: first appetize command is "user code executed"
//  - cached: fromCache
//  - ai: everything else
const classifyLog = (log: SessionLog): "ai" | "cached" | "local" => {
  if (log.appetizeCommands?.at(0)?.toLowerCase() === "user code executed") {
    return "local";
  }
  if (log.fromCache) return "cached";
  return "ai";
};

interface TestUsageRow {
  testId: string;
  name: string;
  runs: number;
  failedRuns: number;
  aiSteps: number;
  avgAiSteps: number;
  // aggregate cache-hit rate across the group: cached / (cached + ai)
  cacheHitRate: number | null;
  avgRuntimeSec: number | null;
}

const WINDOWS: Array<{ key: string; label: string; days: number }> = [
  { key: "7", label: "Last 7d", days: 7 },
  { key: "30", label: "Last 30d", days: 30 },
];

type SortKey = Exclude<keyof TestUsageRow, "testId">;

// Drives both the header row and the sort behaviour. `numeric: false` columns
// sort alphabetically; numeric columns default to descending on first click.
const COLUMNS: Array<{ key: SortKey; label: string; numeric: boolean }> = [
  { key: "name", label: "Test Id", numeric: false },
  { key: "runs", label: "Runs", numeric: true },
  { key: "failedRuns", label: "Failed runs", numeric: true },
  { key: "aiSteps", label: "AI steps", numeric: true },
  { key: "avgAiSteps", label: "Avg AI steps", numeric: true },
  { key: "cacheHitRate", label: "Avg cache hit", numeric: true },
  { key: "avgRuntimeSec", label: "Avg runtime", numeric: true },
];

// ---------------------------------------------------------------------------
// Current-month allowance panel
// ---------------------------------------------------------------------------

const CurrentMonthPanel = () => {
  const { user } = useContext<any>(AuthContext);
  const { data: companyData } = useAtomValue(companyDataAtom(user.companyId));
  const billing = companyData?.billing;

  const stats = useMemo(() => {
    const key = currentUsageKey();
    const cfg = resolveBilling(billing, key);
    if (!cfg) return null;
    const visionRaw =
      (companyData?.usageWithVision?.[key] ?? 0) +
      (companyData?.usageSDKWithVision?.[key] ?? 0);
    const nonVisionRaw =
      (companyData?.usageNonVision?.[key] ?? 0) +
      (companyData?.usageSDKNonVision?.[key] ?? 0);

    const usedWeighted =
      cfg.vision_step_factor * visionRaw +
      cfg.non_vision_step_factor * nonVisionRaw;

    const allowance = cfg.monthly_allowance || 0;
    const usedPct = allowance > 0 ? (usedWeighted / allowance) * 100 : 0;

    // Linear month-to-date projection.
    const now = DateTime.now();
    const dayOfMonth = now.day;
    const daysInMonth = now.daysInMonth ?? 30;
    const projectedWeighted = (usedWeighted / dayOfMonth) * daysInMonth;

    // Overage (in weighted steps) split across the current vision / non-vision
    // mix, then priced with the per-step rates. Estimate only.
    // The split is relative to `usedWeighted`, because `visionRaw` /
    // `nonVisionRaw` are month-to-date counts: dividing by a projected total
    // instead would price only the elapsed fraction of the overage.
    const estimateOverageCost = (weighted: number) => {
      const over = Math.max(0, weighted - allowance);
      if (over <= 0 || usedWeighted <= 0) return 0;
      const fractionOver = over / usedWeighted;
      const visionOver = visionRaw * fractionOver;
      const nonVisionOver = nonVisionRaw * fractionOver;
      return (
        cfg.usd_per_vision_step * visionOver +
        cfg.usd_per_non_vision_step * nonVisionOver
      );
    };

    return {
      visionRaw,
      nonVisionRaw,
      usedWeighted,
      allowance,
      usedPct,
      projectedWeighted,
      currentOverageCost: estimateOverageCost(usedWeighted),
      projectedOverageCost: estimateOverageCost(projectedWeighted),
      projectedOver: Math.max(0, projectedWeighted - allowance),
    };
  }, [billing, companyData]);

  // --- Last 12 months of weighted usage (built from the company usage maps) --
  const [showHistory, setShowHistory] = useState(false);
  const history = useMemo(() => {
    if (!billing) return null;
    const now = DateTime.now();
    const months = Array.from({ length: 12 }, (_, idx) => {
      const m = now.minus({ months: 11 - idx });
      const key = `${String(m.month).padStart(2, "0")}_${m.year}`;
      const cfg = resolveBilling(billing, key);
      const visionRaw =
        (companyData?.usageWithVision?.[key] ?? 0) +
        (companyData?.usageSDKWithVision?.[key] ?? 0);
      const nonVisionRaw =
        (companyData?.usageNonVision?.[key] ?? 0) +
        (companyData?.usageSDKNonVision?.[key] ?? 0);
      const value = cfg
        ? cfg.vision_step_factor * visionRaw +
          cfg.non_vision_step_factor * nonVisionRaw
        : 0;
      // Each month carries its own allowance, so the reference line can step.
      const monthAllowance = cfg?.monthly_allowance ?? 0;
      return {
        key,
        label: m.month === 1 ? m.toFormat("LLL ''yy") : m.toFormat("LLL"),
        value,
        monthAllowance,
        over: Math.max(0, value - monthAllowance),
        isFirst: idx === 0,
        isCurrent: idx === 11,
      };
    });
    const rawMax = Math.max(
      ...months.map((x) => x.value),
      ...months.map((x) => x.monthAllowance),
      1
    );
    const step = niceStep(rawMax / 4);
    const chartMax = Math.ceil(rawMax / step) * step;
    const tickCount = Math.round(chartMax / step);
    const ticks = Array.from({ length: tickCount + 1 }, (_, i) => i * step);
    return {
      months: months.map((x, i, arr) => ({
        ...x,
        pct: (x.value / chartMax) * 100,
        allowancePct: (x.monthAllowance / chartMax) * 100,
        // Previous month's allowance level, for the vertical step connector.
        prevAllowancePct:
          i > 0 ? (arr[i - 1].monthAllowance / chartMax) * 100 : null,
      })),
      chartMax,
      ticks,
    };
  }, [billing, companyData]);

  // --- Email-alert recipients (persisted on the company doc) ----------------
  // Read once (with the doc ref) so add/remove can write back directly.
  const [emails, setEmails] = useState<string[]>([]);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [emailErr, setEmailErr] = useState("");
  const companyDocRef = useRef<DocumentReference | null>(null);

  useEffect(() => {
    let cancelled = false;
    getDocs(
      query(
        collection(customFirestore, "companies"),
        where("id", "==", user.companyId)
      )
    )
      .then((snap) => {
        const docSnap = snap.docs.at(0);
        if (!docSnap || cancelled) return;
        companyDocRef.current = docSnap.ref;
        const raw = docSnap.data().usage_alert_recipients;
        if (Array.isArray(raw)) {
          setEmails(raw.filter((x): x is string => typeof x === "string"));
        }
      })
      .catch((err) => {
        console.error("Failed to load alert recipients", err);
      });
    return () => {
      cancelled = true;
    };
  }, [user.companyId]);

  // Optimistically update, write to Firestore, revert on failure.
  const persistEmails = (next: string[]) => {
    const prev = emails;
    setEmails(next);
    const ref = companyDocRef.current;
    if (!ref) return;
    updateDoc(ref, { usage_alert_recipients: next }).catch((err) => {
      console.error("Failed to save alert recipients", err);
      setEmails(prev);
      setEmailErr("Couldn't save — please try again.");
    });
  };

  const addEmail = () => {
    const v = draft.trim();
    if (!v) {
      setEmailErr("Enter an email address.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      setEmailErr(`"${v}" is not a valid email address.`);
      return;
    }
    if (emails.some((e) => e.toLowerCase() === v.toLowerCase())) {
      setEmailErr("That address is already on the list.");
      return;
    }
    persistEmails([...emails, v]);
    setDraft("");
    setEmailErr("");
  };

  const removeEmail = (i: number) => {
    persistEmails(emails.filter((_, idx) => idx !== i));
  };

  if (!stats) return null;

  const fillColor = stats.usedPct >= 80 ? "#F5A623" : "#4C63FF";

  const monthName = DateTime.now().toFormat("LLLL yyyy");

  return (
    <section className="mb-8">
      <div className="flex items-baseline gap-3 mb-3">
        <h2 className="text-lg font-semibold">Current month</h2>
        <span className="text-sm text-gray-500">{monthName}</span>
      </div>

      <div className="border border-gray-200 rounded-2xl bg-white p-5">
        <div className="flex items-center gap-4">
          <div className="flex-1 h-3.5 rounded-full bg-gray-100 overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${Math.min(stats.usedPct, 100).toFixed(1)}%`,
                background: fillColor,
              }}
            />
          </div>
          <div className="flex items-center text-sm text-gray-600 whitespace-nowrap">
            <b className="text-lg text-gray-900 mr-2">
              {Math.round(stats.usedPct)}%
            </b>
            {fmtInt(stats.usedWeighted)} / {fmtInt(stats.allowance)} AI steps
            <Tooltip
              title="AI steps do not contain cached steps"
              arrow
              placement="top"
            >
              <InfoOutlined sx={{ fontSize: 15, ml: 0.5, color: "#9CA3AF" }} />
            </Tooltip>
          </div>
        </div>

        <p className="text-sm text-gray-600 mt-4 leading-relaxed">
          At the current pace you&apos;ll reach{" "}
          <b className="text-gray-900">{fmtInt(stats.projectedWeighted)}</b> AI
          steps this month —{" "}
          {stats.projectedOver > 0 ? (
            <>
              <b className="text-gray-900">
                {fmtInt(stats.projectedOver)} over booked AI steps
              </b>{" "}
              (≈{" "}
              <b className="text-gray-900">
                {fmtMoney(stats.projectedOverageCost)}
              </b>{" "}
              in estimated overage).
            </>
          ) : (
            <>
              <b>within</b> your {fmtInt(stats.allowance)} booked steps.
            </>
          )}
          <Tooltip
            title="Projection = AI steps used so far this month divided by days elapsed, multiplied by the number of days in the month. Early in the month this can over-estimate, since it assumes today's pace holds for the rest of the month and doesn't account for your running schedule."
            arrow
            placement="top"
          >
            <InfoOutlined
              sx={{
                fontSize: 15,
                ml: 0.5,
                color: "#9CA3AF",
                verticalAlign: "middle",
              }}
            />
          </Tooltip>
        </p>

        <div className="mt-4 pt-4 border-t border-gray-100">
          <span className="text-sm text-gray-600">
            At <b className="text-gray-900">80%</b> usage, email alerts will be
            sent to:
          </span>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {emails.map((e, i) => (
              <span
                key={e}
                className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 py-1 pl-3 pr-1.5 text-sm text-gray-700"
              >
                {e}
                <button
                  onClick={() => {
                    removeEmail(i);
                  }}
                  title="Remove"
                  className="inline-flex h-4 w-4 items-center justify-center rounded-full text-gray-400 hover:bg-red-50 hover:text-[#FF4C4C]"
                >
                  ×
                </button>
              </span>
            ))}
            {adding ? (
              <input
                autoFocus
                type="email"
                value={draft}
                onChange={(e) => {
                  setDraft(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addEmail();
                  } else if (e.key === "Escape") {
                    setAdding(false);
                    setDraft("");
                    setEmailErr("");
                  }
                }}
                placeholder="name@company.com"
                className="w-56 rounded-full border border-[#4C63FF] px-3 py-1 text-sm focus:outline-none"
              />
            ) : (
              <button
                onClick={() => {
                  setAdding(true);
                }}
                title="Add email"
                className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-dashed border-gray-300 text-lg leading-none text-gray-500 hover:border-gray-400 hover:text-gray-700"
              >
                +
              </button>
            )}
          </div>
          {emailErr && (
            <div className="mt-2 text-xs text-[#FF4C4C]">{emailErr}</div>
          )}
        </div>

        {history && (
          <div className="mt-4 border-t border-gray-100 pt-4">
            <button
              onClick={() => {
                setShowHistory((v) => !v);
              }}
              className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
            >
              <span className="text-gray-400">{showHistory ? "▾" : "▸"}</span>
              {showHistory ? "Hide 12-month history" : "Show 12-month history"}
            </button>

            {showHistory && (
              <div className="mt-3 rounded-2xl border border-gray-200 bg-white p-5">
                <div className="flex">
                  {/* Y-axis tick values */}
                  <div className="relative mr-2 h-48 w-9 shrink-0">
                    {history.ticks.map((t) => (
                      <span
                        key={t}
                        className="absolute right-0 translate-y-1/2 text-[10px] text-gray-400"
                        style={{
                          bottom: `${((t / history.chartMax) * 100).toFixed(
                            1
                          )}%`,
                        }}
                      >
                        {fmtAxis(t)}
                      </span>
                    ))}
                  </div>

                  {/* Plot */}
                  <div className="relative h-48 flex-1">
                    {/* Horizontal grid lines */}
                    {history.ticks.map((t) => (
                      <div
                        key={t}
                        className="absolute left-0 right-0 border-t border-gray-200"
                        style={{
                          bottom: `${((t / history.chartMax) * 100).toFixed(
                            1
                          )}%`,
                        }}
                      />
                    ))}

                    {/* Bars (with per-month stepped allowance markers) */}
                    <div className="absolute inset-0 flex items-end gap-2">
                      {history.months.map((m) => (
                        <div
                          key={m.key}
                          className="group relative flex h-full flex-1 items-end justify-center"
                        >
                          {/* Vertical connector where the allowance steps */}
                          {m.prevAllowancePct != null &&
                            m.prevAllowancePct !== m.allowancePct && (
                              <div
                                className="absolute -left-1 z-[1] border-l-2 border-dashed"
                                style={{
                                  bottom: `${Math.min(
                                    m.allowancePct,
                                    m.prevAllowancePct
                                  ).toFixed(1)}%`,
                                  height: `${Math.abs(
                                    m.allowancePct - m.prevAllowancePct
                                  ).toFixed(1)}%`,
                                  borderColor: "#F5A623",
                                }}
                              />
                            )}

                          {/* This month's allowance marker (stepped line) */}
                          <div
                            className="absolute -left-1 -right-1 z-[1] border-t-2 border-dashed"
                            style={{
                              bottom: `${m.allowancePct.toFixed(1)}%`,
                              borderColor: "#F5A623",
                            }}
                          >
                            {m.isFirst && (
                              <span className="absolute left-0 -top-4 bg-white px-1 text-[10px] text-[#F5A623]">
                                booked AI steps
                              </span>
                            )}
                          </div>
                          <div
                            className="pointer-events-none absolute left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-md border border-gray-200 bg-white px-2 py-0.5 text-[10px] text-gray-700 opacity-0 shadow-sm transition-opacity group-hover:opacity-100"
                            style={{
                              bottom: `calc(${m.pct.toFixed(1)}% + 6px)`,
                            }}
                          >
                            {fmtInt(m.value)} AI steps
                            {m.over > 0 && (
                              <span className="ml-1 text-[#F5A623]">
                                +{fmtInt(m.over)} over
                              </span>
                            )}
                          </div>
                          <div
                            className="relative w-3/5 max-w-[28px] rounded-t"
                            style={{
                              height: `${m.pct.toFixed(1)}%`,
                              background: m.isCurrent ? "#4C63FF" : "#A9B4F7",
                            }}
                          >
                            {m.over > 0 && (
                              <div
                                className="absolute left-0 right-0 top-0 rounded-t"
                                style={{
                                  height: `${((m.over / m.value) * 100).toFixed(
                                    1
                                  )}%`,
                                  background: "#F5A623",
                                }}
                              />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Month labels (aligned under the plot, past the y-axis) */}
                <div className="mt-2 flex gap-2 pl-11">
                  {history.months.map((m) => (
                    <span
                      key={m.key}
                      className="flex-1 text-center text-[10px] text-gray-400"
                    >
                      {m.label}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Per-test aggregation table (SDK sessions)
// ---------------------------------------------------------------------------

const AllTestsPanel = () => {
  const { user } = useContext<any>(AuthContext);
  const [windowKey, setWindowKey] = useState("7");
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<TestUsageRow[]>([]);
  // Per-window result cache (keyed by company + window) so re-selecting a range
  // already viewed is instant — no refetch. Lives for the component's lifetime.
  const cacheRef = useRef<Map<string, TestUsageRow[]>>(new Map());
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" }>({
    key: "aiSteps",
    dir: "desc",
  });
  const [search, setSearch] = useState("");

  const handleSort = (col: (typeof COLUMNS)[number]) => {
    setSort((prev) =>
      prev.key === col.key
        ? { key: col.key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key: col.key, dir: col.numeric ? "desc" : "asc" }
    );
  };

  const sortedRows = useMemo(() => {
    const { key, dir } = sort;
    const factor = dir === "asc" ? 1 : -1;
    const q = search.trim().toLowerCase();
    const filtered = q
      ? rows.filter((r) => r.name.toLowerCase().includes(q))
      : rows;
    return [...filtered].sort((a, b) => {
      if (key === "name") {
        return factor * String(a.name).localeCompare(String(b.name));
      }
      // Numeric columns — keep null values (no data) last either way.
      const an = a[key];
      const bn = b[key];
      if (an == null && bn == null) return 0;
      if (an == null) return 1;
      if (bn == null) return -1;
      return factor * (an - bn);
    });
  }, [rows, sort, search]);

  useEffect(() => {
    let cancelled = false;

    // Serve from cache if this window was already loaded — no refetch.
    const cacheKey = `${String(user.companyId)}|${windowKey}`;
    const cached = cacheRef.current.get(cacheKey);
    if (cached) {
      setRows(cached);
      setLoading(false);
      return;
    }

    setLoading(true);

    const load = async () => {
      const win = WINDOWS.find((w) => w.key === windowKey);
      const days = win ? win.days : 7;
      const cutoff = DateTime.now().minus({ days }).toJSDate();

      const sessionsSnap = await getDocs(
        query(
          collection(customFirestore, "sessions"),
          where("organisationId", "==", user.companyId),
          where("createdAt", ">=", cutoff)
        )
      );

      // Accumulate per test id.
      const acc = new Map<
        string,
        {
          runs: number;
          failedRuns: number;
          aiSteps: number;
          cached: number;
          cacheable: number;
          runtimeSum: number;
          runtimeCount: number;
        }
      >();

      sessionsSnap.docs.forEach((d) => {
        const s = d.data() as Session;
        const testId = s.testId ?? "__adhoc__";
        const entry = acc.get(testId) ?? {
          runs: 0,
          failedRuns: 0,
          aiSteps: 0,
          cached: 0,
          cacheable: 0,
          runtimeSum: 0,
          runtimeCount: 0,
        };

        entry.runs += 1;
        if (s.status === "failed") entry.failedRuns += 1;

        (s.interactionLogs ?? []).forEach((log) => {
          const kind = classifyLog(log);
          if (kind === "ai") {
            entry.aiSteps += 1;
            entry.cacheable += 1;
          } else if (kind === "cached") {
            entry.cached += 1;
            entry.cacheable += 1;
          }
        });

        if (s.createdAt && s.lastUpdatedAt) {
          const dur =
            (s.lastUpdatedAt.toMillis() - s.createdAt.toMillis()) / 1000;
          if (dur >= 0 && dur < 60 * 60) {
            entry.runtimeSum += dur;
            entry.runtimeCount += 1;
          }
        }

        acc.set(testId, entry);
      });

      const result: TestUsageRow[] = Array.from(acc.entries()).map(
        ([testId, e]) => ({
          testId,
          name: testId === "__adhoc__" ? "Ad-hoc / no test id" : testId,
          runs: e.runs,
          failedRuns: e.failedRuns,
          aiSteps: e.aiSteps,
          avgAiSteps: e.runs ? e.aiSteps / e.runs : 0,
          cacheHitRate: e.cacheable ? (e.cached / e.cacheable) * 100 : null,
          avgRuntimeSec: e.runtimeCount ? e.runtimeSum / e.runtimeCount : null,
        })
      );

      if (!cancelled) {
        cacheRef.current.set(cacheKey, result);
        setRows(result);
        setLoading(false);
      }
    };

    load().catch((err) => {
      console.error("Failed to load usage sessions", err);
      if (!cancelled) {
        setRows([]);
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [user.companyId, windowKey]);

  return (
    <section>
      <div className="flex items-baseline gap-3 mb-3">
        <h2 className="text-lg font-semibold">All sessions</h2>
        <span className="text-sm text-gray-500">
          {rows.length} tests ·{" "}
          {WINDOWS.find((w) => w.key === windowKey)?.label.toLowerCase()}
        </span>
      </div>

      <div className="flex items-center gap-2 mb-3">
        {WINDOWS.map((w) => (
          <button
            key={w.key}
            onClick={() => {
              setWindowKey(w.key);
            }}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
              windowKey === w.key
                ? "border-[#4C63FF] text-[#4C63FF] bg-[#4C63FF]/5"
                : "border-gray-200 text-gray-500 hover:text-gray-700"
            }`}
          >
            {w.label}
          </button>
        ))}
        <input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
          }}
          placeholder="Search by test id…"
          className="ml-auto w-60 px-3 py-1.5 rounded-full text-sm border border-gray-200 focus:border-[#4C63FF] focus:outline-none"
        />
      </div>

      <div className="border border-gray-200 rounded-2xl bg-white overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-16">
            <CircularProgress />
            <span className="text-sm text-gray-500">Aggregating the data…</span>
          </div>
        ) : sortedRows.length === 0 ? (
          <div className="py-16 text-center text-gray-400 text-sm">
            {search.trim()
              ? "No test ids match your search."
              : "No sessions in this period."}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase text-gray-500 tracking-wide">
                {COLUMNS.map((col) => {
                  const active = sort.key === col.key;
                  const alignRight = col.key !== "name";
                  return (
                    <th
                      key={col.key}
                      className={`font-medium py-3 ${
                        alignRight ? "px-2 text-right" : "px-4 text-left"
                      }`}
                    >
                      <button
                        onClick={() => {
                          handleSort(col);
                        }}
                        className={`inline-flex items-center gap-1 uppercase tracking-wide ${
                          alignRight ? "flex-row-reverse" : ""
                        } ${active ? "text-[#4C63FF]" : "hover:text-gray-700"}`}
                      >
                        <span>{col.label}</span>
                        <span className="w-3 text-[10px] leading-none">
                          {active ? (sort.dir === "asc" ? "▲" : "▼") : ""}
                        </span>
                      </button>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((r) => (
                <tr
                  key={r.testId}
                  className="border-t border-gray-100 hover:bg-gray-50"
                >
                  <td className="px-4 py-3 text-gray-800">
                    <Tooltip title={r.name} arrow placement="top">
                      <div className="max-w-[420px] truncate">{r.name}</div>
                    </Tooltip>
                  </td>
                  <td className="px-2 py-3 text-right text-gray-600 tabular-nums">
                    {r.runs}
                  </td>
                  <td
                    className="px-2 py-3 text-right tabular-nums"
                    style={{ color: r.failedRuns ? "#FF4C4C" : "#9CA3AF" }}
                  >
                    {r.failedRuns}
                  </td>
                  <td className="px-2 py-3 text-right text-gray-800 tabular-nums">
                    {fmtInt(r.aiSteps)}
                  </td>
                  <td className="px-2 py-3 text-right text-gray-600 tabular-nums">
                    {r.avgAiSteps.toFixed(1)}
                  </td>
                  <td className="px-2 py-3 text-right text-gray-600 tabular-nums">
                    {r.cacheHitRate == null
                      ? "—"
                      : `${Math.round(r.cacheHitRate)}%`}
                  </td>
                  <td className="px-2 py-3 text-right text-gray-600 tabular-nums">
                    {fmtDur(r.avgRuntimeSec)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------

const UsagePanel = () => {
  const { user } = useContext<any>(AuthContext);
  const companyQuery = useAtomValue(companyDataAtom(user.companyId));
  const companyData = companyQuery.data;
  // Don't decide "no billing" until the company doc has actually loaded —
  // otherwise the contact-us message flashes before the data arrives.
  const isLoading = companyQuery.isLoading || companyQuery.isPending;
  const hasBilling = !!companyData?.billing;

  return (
    <PlainCard>
      <div className="h-full overflow-auto">
        <h1 className="text-3xl pl-1 pb-4 font-bold">Usage</h1>
        {isLoading ? (
          <div className="flex items-center justify-center py-24">
            <CircularProgress />
          </div>
        ) : hasBilling ? (
          <>
            <CurrentMonthPanel />
            <AllTestsPanel />
          </>
        ) : (
          <div className="flex flex-col items-center justify-center gap-2 py-24 text-center">
            <p className="text-gray-700">
              The usage dashboard isn&apos;t enabled for your workspace yet.
            </p>
            <p className="text-sm text-gray-500">
              Please contact us to enable it.
            </p>
          </div>
        )}
      </div>
    </PlainCard>
  );
};

export default UsagePanel;
